import { ShieldOff, ArrowLeft, PackageX } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '@/contexts/PermissionContext';
import { useAuth } from '@/contexts/AuthContext';
import { useModules } from '@/contexts/ModuleContext';

interface ProtectedRouteProps {
  /** Un permesso richiesto, oppure un elenco di cui basta ALMENO UNO (PM-017). */
  permission: string | readonly string[];
  /**
   * Task #907: when set, the module must be enabled for the tenant for the
   * route to render — even for admins. This closes the gap where an admin
   * could navigate directly to a page belonging to a disabled module just
   * by typing its URL.
   */
  moduleKey?: string;
  children: React.ReactNode;
}

export default function ProtectedRoute({ permission, moduleKey, children }: ProtectedRouteProps) {
  const { user } = useAuth();
  const { hasPermission, hasAnyPermission, loading } = usePermissions();
  const { isModuleEnabled, loading: modulesLoading } = useModules();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Module gate runs first so it applies to admins too. The master tenant
  // shortcut inside `isModuleEnabled` keeps the platform-wide owner working.
  if (moduleKey) {
    if (modulesLoading) {
      return (
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      );
    }
    if (!isModuleEnabled(moduleKey)) {
      return (
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <PackageX className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Modulo non attivo</h2>
            <p className="text-muted-foreground mb-6">
              Il modulo necessario per questa sezione non è attivo per il tuo tenant. Chiedi al tenant master di abilitarlo.
            </p>
            <button
              onClick={() => navigate(`/${tenantSlug}/dashboard`)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              Torna alla Dashboard
            </button>
          </div>
        </div>
      );
    }
  }

  if (isAdmin) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const allowed = typeof permission === 'string' ? hasPermission(permission) : hasAnyPermission([...permission]);
  if (!allowed) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-6">
            <ShieldOff className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">Accesso negato</h2>
          <p className="text-muted-foreground mb-6">
            Non hai i permessi necessari per accedere a questa sezione. Contatta l'amministratore del tuo tenant per richiedere l'accesso.
          </p>
          <button
            onClick={() => navigate(`/${tenantSlug}/dashboard`)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
